<template>
  <div style="{width: 100%}">
    <div v-if="collectionSelected" class="background-container">
          <div class="header-container">
            <div class="header-section">
              <i
                class="icon-style"
                :class="getIconClass(collectionSelected.id)"
                style="font-size: 14px"
              ></i>
              <h6 class="mb-0 collection-name">{{ collectionSelected.id }}</h6>
            </div>
            <div class="header-section ml-auto search-container">
              <input
                type="text"
                class="form-control search-input"
                v-model="searchText"
                placeholder="Doc ID"
                @keyup.enter="searchById"
              />
              <i class="fa fa-search search-icon" @click="searchById"></i>
            </div>
            <div class="header-section">
              <button
                class="btn filter-btn d-flex align-items-center"
                ref="toggleButton"
                ClickOutside="clickOutside"
              >
                <i class="ri-filter-fill filter-icon"></i> Filter
              </button>
              <el-popover
                placement="bottom-end"
                trigger="click"
                :width="fit-content"
                ref="popover"
                :virtual-ref="toggleButton"
                virtual-triggering
              >
                <template #reference> </template>
                <div
                  class="filter-content"
                  v-for="index in numFilters"
                  :key="index"
                >
                  <div class="filter-row mb-4">
                    <span>WHERE</span>

                    <div class="filter-label-input mr-2">
                      <el-select
                        :model-value="inputFilters[index - 1].field"
                        @update:model-value="value => inputFilters[index - 1].field = value"
                        :teleported="false"
                        filterable
                        allow-create
                        :class="{ 'is-invalid': inputFilters[index - 1].invalidField }"
                      >
                        <el-option
                          v-for="item in tableColumns.filter(column => column.property !== 'id')"
                          :key="item.label"
                          :label="item.label"
                          :value="item.label"
                        />
                      </el-select>
                      <span
                        v-show="inputFilters[index - 1] && inputFilters[index - 1].invalidField"
                        class="invalid-input-error"
                        >*Invalid field</span
                      >
                    </div>
                    <el-select
                      class="mr-2"
                      :model-value="inputFilters[index - 1].condition"
                      @update:model-value="value => inputFilters[index - 1].condition = value"
                      :teleported="false"
                    >
                      <el-option
                        v-for="item in compareOptions"
                        :key="item.value"
                        :label="item.label"
                        :value="item.value"
                      />
                    </el-select>
                    <el-select
                      class="mr-2"
                      :model-value="inputFilters[index - 1].dataType"
                      @update:model-value="value => inputFilters[index - 1].dataType = value"
                      :teleported="false"
                    >
                      <el-option
                        v-for="item in dataTypeOptions"
                        :key="item"
                        :label="item"
                        :value="item"
                      />
                    </el-select>
                    <div class="filter-label-input">
                      <el-input
                        class="filter"
                        @update:model-value="value => inputFilters[index - 1].target = value"
                        :model-value="inputFilters[index - 1].target"
                        :class="{ 'is-invalid': inputFilters[index - 1].invalidTarget }"
                      />
                      <span
                        v-show="inputFilters[index - 1].invalidTarget"
                        class="invalid-input-error"
                        >*Invalid value</span
                      >
                    </div>
                    <span
                      @click="removeFilterByIndex(index - 1)"
                      class="ml-1 text-btn"
                      v-if="index > 1"
                      ><i class="ri-delete-bin-line ml-1"></i
                    ></span>
                  </div>
                </div>
                <div class="filter-actions mt-3">
                  <button @click="addAnotherFilter" class="btn add-filter-btn">
                    + Add filter
                  </button>
                  <div class="action-buttons">
                    <button @click="clearFilters" class="btn action-btn">
                      Clear All
                    </button>
                    <button @click="discardFilters" class="btn action-btn">
                      Discard
                    </button>
                    <button
                      @click="applyFilters"
                      class="btn action-btn apply-btn"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </el-popover>
              <button @click="reset" class="btn reset-btn">
                <i class="ri-refresh-line"></i>
              </button>
              <button @click="addDocument" class="btn add-btn">Add</button>
              <button @click="deleteDocuments" class="btn delete-btn">
                Delete
              </button>
            </div>
          </div>
          <div v-if="!loading" class="database-content">
            <div v-if="documents.length">
              <!--Database Table -->
              <el-table
                ref="table"
                :data="documents"
                class="table background-container"
                style="width: 100%"
                @selection-change="handleSelectionChange"
                @sort-change="sortTable"
                @row-click="handleRowClick"
                :default-sort="defaultSort"
              >
                <el-table-column type="selection" width="55" />
                <el-table-column
                  v-for="column in tableColumns"
                  :key="column.property"
                  :label="column.label"
                  show-overflow-tooltip
                  :sortable="column.property !== 'id' ? 'custom' : false"
                  :prop="column.property"
                  sorted="false"
                >
                  <template #default="scope">
                    {{ formatFirestoreTimestamp(scope.row[column.property]) }}
                  </template>
                </el-table-column>
              </el-table>
              <div class="load-more-container" v-if="!allDataLoaded">
                <p
                  @click="fetchNextPage(collectionSelected.id, sortQuery, inputFilters, inputFiltersApplied)"
                  class="load-more-text"
                >
                  Load more
                </p>
              </div>
              <div class="load-more-container" v-else>
                <p class="all-loaded-text">All documents loaded</p>
              </div>
            </div>
            <div v-else>
              <el-empty description="No documents" />
            </div>
          </div>
          <Loading
        :active="deletingDocuments"
        :height="40"
        :width="40"
        :is-full-page="true"
        loader="bars"
        class="loader"
      />
        </div>
        <el-skeleton class="mt-5" v-else :rows="15" animated />

        <DocumentDrawer
      :collection="collectionSelected"
      ref="drawerRef"
    ></DocumentDrawer>
  </div>
</template>

<script>
import { onMounted, ref, watch } from 'vue'
import getIconClass from '../../../services/getIconBasedOnCollectionID'
import getAllDocumentsWithPagination from '../../../services/getAllDocumentsWithPagination'
import { format } from 'date-fns'
import { ElMessageBox } from 'element-plus'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/css/index.css'
import DocumentDrawer from './DocumentDrawer.vue'
import { doc, getDoc } from 'firebase/firestore'
import firebase from '../../../../firebase'
import { useRouter } from 'vue-router'

export default {
  name: 'CollectionsView',
  props: ['collection'],
  components: { DocumentDrawer, Loading },
  setup (props, context) {
    const collectionSelected = ref(null)
    const document = ref(null)
    const { documents, pageSize, allDataLoaded, loading, fetchDocuments, fetchNextPage, getDocumentSnapshot, deleteDocumentsByDocumentIds } = getAllDocumentsWithPagination()
    const selections = ref([])
    const searchText = ref('')
    const drawerRef = ref(null)
    const sortQuery = ref(null)
    const defaultSort = ref({ prop: 'null', order: 'null' })
    const inputFiltersApplied = ref(false)
    const deletingDocuments = ref(false)
    const tableColumns = ref([])
    const popover = ref(null)
    const toggleButton = ref(null)
    const router = useRouter()

    const setupCollectionPage = (newCollection) => {
      const collectionDocRef = doc(firebase.db, 'AllCollections', newCollection)
      getDoc(collectionDocRef).then((snapshot) => {
        if (snapshot.exists()) {
          document.value = { id: snapshot.id, ...snapshot.data() }
          collectionSelected.value = document.value
          tableColumns.value = getTableColumns()
          fetchDocuments(newCollection)
        }
      })
    }

    watch(() => props.collection, (newCollection, oldCollection) => {
      handleNewCollectionSelected()
      if (newCollection !== oldCollection) {
        setupCollectionPage(newCollection)
      }
    })

    onMounted(() => {
      if (props.collection) {
        setupCollectionPage(props.collection)
      }
    })

    const handleNewCollectionSelected = () => {
      selections.value = []
      sortQuery.value = {}
      clearFilters()
      inputFiltersApplied.value = false
      defaultSort.value = { prop: 'null', order: 'null' }
      searchText.value = []
    }

    const getTableColumns = () => {
      if (!collectionSelected.value || !collectionSelected.value.fields) return []
      let keysSorted = Object.keys(collectionSelected.value.fields).sort((a, b) => {
        if (a === 'id') return -1
        if (b === 'id') return 1
        return a.localeCompare(b)
      })
      keysSorted = keysSorted.filter(item => item !== 'password')
      return keysSorted.map(key => ({
        property: key,
        label: key.charAt(0) + key.slice(1)
      }))
    }

    const handleSelectionChange = (selection) => {
      selections.value = selection.map(doc => doc.id)
    }

    const refetchDocumentsForCurrentCollection = () => {
      searchText.value = ''
      selections.value = []
      console.log('refetching')
      fetchDocuments(collectionSelected.value.id, sortQuery.value, inputFilters.value, inputFiltersApplied.value)
    }

    const formatFirestoreTimestamp = (timestamp) => {
      if (timestamp && timestamp.toDate) {
        return format(timestamp.toDate(), 'yyyy-MM-dd HH:mm:ss')
      }
      return timestamp
    }

    const handleRowClick = (row, column, event) => {
      if (column.type === 'selection') return
      router.push({ name: 'admin.document', params: { collectionId: props.collection, docId: row.id } })
    }

    const searchById = () => {
      inputFiltersApplied.value = false
      selections.value = []
      if (searchText.value.length) {
        getDocumentSnapshot(collectionSelected.value.id, searchText.value)
      } else {
        fetchDocuments(collectionSelected.value.id)
      }
    }

    const reset = () => {
      inputFiltersApplied.value = false
      searchText.value = ''
      sortQuery.value = {}
      selections.value = []
      clearFilters()
      defaultSort.value = { prop: 'null', order: 'null' }
      fetchDocuments(collectionSelected.value.id)
    }

    // Filter
    const inputFilters = ref([{ field: '', condition: '==', target: '', dataType: 'string', invalidField: false, invalidTarget: false }])
    const numFilters = ref(1)
    const dataTypeOptions = [
      'string',
      'number',
      'boolean'
    ]
    const compareOptions = [
      {
        value: '==',
        label: 'Equal to'
      },
      {
        value: '!=',
        label: 'Not equal to'
      },
      {
        value: '>',
        label: 'Greater than'
      },
      {
        value: '<',
        label: 'Less than'
      },
      {
        value: '>=',
        label: 'Greater than or equal to'
      },
      {
        value: '<=',
        label: 'Less than or equal to'
      }
    ]

    const addAnotherFilter = () => {
      inputFilters.value.push({ field: '', condition: '==', target: '', dataType: 'string', invalidField: false, invalidTarget: false })
      numFilters.value++
    }

    const clearFilters = () => {
      inputFilters.value = [{ field: '', condition: '==', target: '', dataType: 'string', invalidField: false, invalidTarget: false }]
      numFilters.value = 1
    }

    const closePopover = (shouldClearFilters = true) => {
      if (shouldClearFilters) {
        clearFilters()
      }
      popover.value.hide()
    }

    const removeFilterByIndex = (index) => {
      inputFilters.value.splice(index, 1)
      numFilters.value--
    }

    const sortTable = (column, prop, order) => {
      const sortDirec = column.order === 'ascending' ? 'asc' : 'desc'
      defaultSort.value = { prop: column.column.rawColumnKey, order: column.order }
      sortQuery.value = { field: column.column.rawColumnKey, direction: sortDirec }
      fetchDocuments(collectionSelected.value.id, sortQuery.value, inputFilters.value, inputFiltersApplied.value)
    }

    const applyFilters = () => {
      let invalidFilters = false
      inputFilters.value.forEach((filter, index) => {
        inputFilters.value[index].invalidTarget = false
        inputFilters.value[index].invalidField = false
        if (!filter.field.length) {
          inputFilters.value[index].invalidField = true
          invalidFilters = true
        }
        if (filter.dataType === 'number') {
          if (!filter.target.length || isNaN(filter.target)) {
            inputFilters.value[index].invalidTarget = true
            invalidFilters = true
          }
        }
        if (filter.dataType === 'boolean') {
          if (!['true', 'false'].includes(filter.target)) {
            inputFilters.value[index].invalidTarget = true
            invalidFilters = true
          }
        }
      })
      if (invalidFilters) {
        return
      }
      closePopover(false)
      inputFiltersApplied.value = true
      fetchDocuments(collectionSelected.value.id, sortQuery.value, inputFilters.value, inputFiltersApplied.value)
    }

    const discardFilters = () => {
      closePopover()
      if (inputFiltersApplied.value) {
        inputFiltersApplied.value = false
        fetchDocuments(collectionSelected.value.id, sortQuery.value, inputFilters.value, inputFiltersApplied.value)
      }
    }

    const deleteDocuments = async () => {
      if (!selections.value.length) {
        return
      }
      ElMessageBox.confirm('Are you sure you want to delete the selected documents?', 'Delete Documents', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        deletingDocuments.value = true
        await deleteDocumentsByDocumentIds(collectionSelected.value.id, selections.value)
        searchText.value = ''
      }).catch((e) => {
      }).finally(() => {
        deletingDocuments.value = false
      })
    }

    const addDocument = () => {
      drawerRef.value.openDrawer()
    }

    return {
      collectionSelected,
      getIconClass,
      documents,
      pageSize,
      allDataLoaded,
      loading,
      selections,
      searchText,
      tableColumns,
      formatFirestoreTimestamp,
      searchById,
      reset,
      inputFilters,
      numFilters,
      dataTypeOptions,
      compareOptions,
      addAnotherFilter,
      clearFilters,
      removeFilterByIndex,
      sortTable,
      applyFilters,
      popover,
      toggleButton,
      fetchNextPage,
      inputFiltersApplied,
      discardFilters,
      deletingDocuments,
      drawerRef,
      deleteDocuments,
      handleSelectionChange,
      handleRowClick,
      addDocument,
      refetchDocumentsForCurrentCollection
    }
  }
}
</script>

<style scoped>
.database-screen {
  width: 80%;
  background-color: white;
}
.placeholder-container {
  height: 100%;
}

.icon-large {
  font-size: 3rem;
}

.search-input {
  height: 30px;
  min-width: 300px;
}

.collection-name {
  margin-left: 8px;
}

.icon-style {
  font-size: 18px !important;
  vertical-align: baseline !important;
}

.filter-btn {
  background-color: rgba(0, 128, 0, 0.1);
  color: green;
  margin-left: auto;
}

.filter-btn:hover {
  background-color: rgba(0, 128, 0, 0.214);
  color: green;
}

.reset-btn {
  background-color: #e1e5e8;
  padding-left: 18px;
}

.add-btn {
  background: green;
  color: white;
}

.add-btn:hover {
  background: darkgreen;
  color: white;
}

.delete-btn {
  background: rgb(101, 97, 97);
  color: white;
}

.delete-btn:hover {
  background: rgb(86, 84, 84);
  color: white;
}

.btn {
  margin-left: 8px;
}

.text-btn {
  cursor: pointer;
}

.btn.filter-btn {
  display: inline-flex;
}

.header-container {
  display: flex;
  align-items: center;
  justify-content: start;
  width: 100%;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-color);
  padding: 15px 20px;
  background-color: var(--light-grey);
}

.header-section {
  display: flex;
  align-items: center;
}

.search-container {
  margin-left: auto;
  position: relative;
}

.search-input {
  width: auto;
  padding-right: 30px;
}

.filter-icon {
  vertical-align: baseline !important;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
}

.database-content {
  height: fit-content;
  overflow-y: auto;
}

.load-more-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: var(--light-grey);
}

.load-more-text {
  cursor: pointer;
  color: #007bff;
  font-weight: bold;
  margin-top: 15px;
  padding-right: 10px;
}

.all-loaded-text {
  color: grey;
  margin-top: 15px;
  padding-right: 10px;
}

.table {
  margin-bottom: 0;
}

.filter-input,
.filter-select {
  flex-grow: 1;
  margin-right: 8px;
}

.filter-select-small {
  max-width: 100px;
}

.filter-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid var(--border-color);
  padding-top: 15px;
}

.add-filter-btn {
  background-color: transparent;
  color: green;
  border: none;
  margin-left: 0;
  font-size: 13px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-btn {
  background-color: #e1e5e8;
  color: black;
  font-size: 13px !important;
}

.apply-btn {
  background-color: green;
  color: white;
}

.apply-btn:hover {
  background-color: darkgreen;
  color: white;
}

.filter.form-control {
  border-radius: 3px;
  font-size: 14px;
  height: fit-content;
  padding: 0 5px;
  line-height: 35px;
}

.filter-content {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  font-size: 13px !important;
}

.filter-row span {
  white-space: nowrap;
  margin-right: 8px;
}

.filter {
  flex: 1;
  margin-right: 8px;
}

.filter-select {
  min-width: 190px;
}

.filter-content {
  padding-left: 0;
  padding-right: 0;
}

.filter-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid var(--border-color);
  padding-top: 15px;
  margin-left: -15px;
  margin-right: -15px;
  padding-left: 15px;
  padding-right: 15px;
}

.filter-label-input {
  position: relative;
}

.filter-label-input .is-invalid + .invalid-input-error {
  visibility: visible;
}

.invalid-input-error {
  color: #ff4d4f;
  font-size: 11px;
  position: absolute;
  bottom: -20px;
  left: 0;
  visibility: hidden;
}
</style>

<style>
[dir="ltr"] label {
  margin-bottom: 0;
}

.table .el-table__row td {
  padding: 20px;
}

.el-table__cell.is-leaf {
  background-color: #f0f1f3 !important;
  color: black;
  font-weight: 400;
}

.el-table .cell.el-tooltip {
  padding-right: 30px;
  color: grey;
}

.el-table__cell.is-leaf .el-checkbox__inner {
  visibility: hidden;
  cursor: not-allowed;
}

.el-table__row {
  cursor: pointer;
}
.el-message-box {
  min-width: 600px !important;
}
.el-message-box__wrapper {
  border-radius: 8px;
}

.el-message-box__header {
  color: white;
  font-size: 14px;
}

.el-message-box__title {
  font-size: 15px !important;
  font-weight: 500;
  margin-bottom: 10px !important;
}

.el-message-box__content {
  font-size: 14px;
  color: #606266;
}

.el-message-box__container {
  background-color: #fde2e2;
  border-radius: 4px;
  padding: 20px;
  color: #8c1d18;
  margin-bottom: 10px;
}

.el-message-box__message p {
  margin-left: 10px !important;
}

.el-message-box__btns button {
  font-size: 12px;
  border-radius: 4px;
  padding: 8px 20px;
}

.el-message-box__btns .el-button--default {
  background-color: #fff;
  color: #606266;
  border: 1px solid #dcdfe6;
}

.el-message-box__btns .el-button--primary {
  background-color: #d32f2f !important;
  border-color: #d32f2f !important;
  color: white;
}

.el-message-box__btns .el-button--primary:hover {
  background-color: #d32f2f;
  border-color: #d32f2f;
  color: white;
}

.el-message-box__input {
  margin-top: 15px;
}

.el-message-box__input .el-input__inner:focus {
  border-color: #f56c6c !important;
}

.el-message-box__btns button:hover {
  opacity: 0.8;
}
.el-icon.el-message-box__status.el-message-box-icon--warning {
  left: 10px;
  margin-right: 10px;
}

.load-more-container .load-more-text {
    color: var(--accent-color) !important;
}

</style>
